import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import SquarePaymentForm from './SquarePaymentForm';
import { OTP_ENABLED } from './PhoneOtpGate';
import { MembershipNotice, ProviderReviewNotice } from './checkoutUi';
import ProductLearnMore from './ProductLearnMore';
import { useSchoolPrideConfetti } from '@/lib/confetti';
import { WL_ADDITIVES, ANSWER_TO_KEY, additiveOf, findCompound } from '@/lib/wlAdditives';
import { apiService } from '@/services/api';
import { otoCardOnFile } from '@/data/conciergeOto';
import OtoCardSaveNotice from './OtoCardSaveNotice';

// §catalog (FE_PRD catalog-driven meds) — GLP-1 / weightloss checkout.
// Two phases (Eden-style):
//   1. RECOMMEND — "your provider will likely recommend" the single resolved
//      product, with about-compounded reassurance + a weight-journey outlook.
//      "Modify your plan" opens a modal listing every weight-loss SKU.
//   2. PAY — polished order summary (benefit checkmarks + trust banners),
//      "Frequently added together" additives, and payment.
//
// Additives resolve compound-first: a "Semaglutide + <additive>" SKU at the same
// dose+supply swaps the primary (one compounded medId); else a standalone SKU is
// added as its own line. Either way it rides the SAME weight-loss visit (no new
// intake). NAD+/glutathione (need their own intake) are excluded.
//
// On pay we send selectedMedIds:[activePrimary, ...separateAdditives] to the
// payment endpoint (BE prices from catalog, eeabef5) and create-visit-v2 (§5).
// Additive/base detection is metadata-driven (additiveKeys/baseProductKey/
// requiresSeparateIntake, BE PRs #182-183); standalone SKUs flagged
// requiresSeparateIntake (nad/glutathione/…) are never offered here.
// Still missing BE-side: b6/b12/ondansetron compound SKUs in the weightloss
// catalog (southend seed) — until then those resolve standalone-only.

const fmtUsd = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;

function resolveBase(meds, molecule, isNewPatient) {
  const byMol = molecule ? meds.filter((m) => `${m.name} ${m.medication}`.toLowerCase().includes(molecule)) : meds.slice();
  const pool = (byMol.length ? byMol : meds).filter((m) => !additiveOf(m));
  if (!pool.length) return null;
  const starting = pool.filter((m) => /starting dose/i.test(m.name || '')).sort((a, b) => (a.price || 0) - (b.price || 0));
  if (isNewPatient && starting.length) return starting[0];
  return pool.slice().sort((a, b) => (a.price || 0) - (b.price || 0))[0];
}
function chosenKeys(answers) {
  const v = answers?.['side-effects'];
  const map = v && v.additives ? v.additives : {};
  return new Set(Object.values(map).map((x) => ANSWER_TO_KEY[x]).filter(Boolean));
}
// Weight-journey outlook: current weight (from answers) minus the goal range's
// upper bound → an achievable target to motivate at checkout.
function weightOutlook(answers) {
  const wp = answers?.['weight-pounds'];
  const current = Number(wp?.weight || wp?.rawAnswer?.weight || 0);
  const goalStr = String(answers?.['weight-loss-goal'] || '');
  const nums = (goalStr.match(/\d+/g) || []).map(Number);
  const loss = nums.length ? Math.max(...nums) : 0;
  if (!current || !loss) return null;
  return { current, target: Math.max(0, current - loss), loss };
}

const Check = () => (
  <svg className="w-4 h-4 text-[#2d8a5f] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);
const MedImage = ({ item, size = 'w-16 h-16' }) => {
  const [failed, setFailed] = useState(false);
  if (item?.imageUrl && !failed) {
    return <img src={item.imageUrl} alt={item.name || 'Medication'} onError={() => setFailed(true)} className={`${size} rounded-xl object-cover flex-shrink-0 bg-[#f0faf4]`} />;
  }
  return (
    <div className={`${size} rounded-xl bg-gradient-to-br from-[#e8f5ee] to-[#cfeadd] flex items-center justify-center flex-shrink-0`} aria-hidden="true">
      <svg className="w-8 h-8 text-[#2d8a5f]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 3h6M10 3v4.5L5.6 16a3 3 0 002.6 4.5h7.6a3 3 0 002.6-4.5L14 7.5V3M7.5 13h9" />
      </svg>
    </div>
  );
};

const GlpCatalogCheckoutStep = ({
  onComplete, isProcessing, answers, setLoading, clearSavedProgress, primaryGoal, currentStep, timestamp, email,
}) => {
  const stagingTest =
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('belugaEnv') === 'staging';

  const [wlMeds, setWlMeds] = useState(null);
  const [standaloneCat, setStandaloneCat] = useState([]);
  const [error, setError] = useState('');
  const [compoundKey, setCompoundKey] = useState(null);
  const [separateIds, setSeparateIds] = useState([]);
  const [manualBaseId, setManualBaseId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [phase, setPhase] = useState('recommend'); // 'recommend' | 'pay'
  // §otp: phone is verified upstream on the contact slide (BasicContactStep);
  // the token rides the basic-contact answer to here and is relayed to payment.
  const phoneVerifyToken = answers?.['basic-contact']?.phoneVerifyToken || undefined;

  // §lead-capture: on reaching checkout we have name/email/phone/address — persist
  // the lead so the dashboard can re-target if the patient abandons before paying.
  // Fire-and-forget, flag-gated (dark until the BE /leads endpoint ships).
  useEffect(() => {
    if (!OTP_ENABLED) return;
    const c = answers?.['basic-contact'] || {};
    const s = answers?.['shipping-address'] || answers?.shipping || answers?.address || {};
    if (!c.email && !c.phone) return;
    apiService.captureLead({
      name: c.name, email: c.email, phone: c.phone,
      address1: s.address1, city: s.city, state: s.state, zip: s.zip || s.zipcode,
      visitType: 'weightloss', flow: primaryGoal, lastStep: 'checkout',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const molecule = (answers?.['medication-selection'] || '').toLowerCase();
  const glpMeds = answers?.['current-glp-medications'];
  const isNewPatient = Array.isArray(glpMeds) ? glpMeds.includes('none') : true;
  const moleculeLabel = molecule ? molecule.charAt(0).toUpperCase() + molecule.slice(1) : 'GLP-1';
  const outlook = useMemo(() => weightOutlook(answers), [answers]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      // No pharmacy filter — the modal and the additive cross-reference must
      // see every fulfilling pharmacy (beluga, hallandale, southend, …).
      apiService.getMedications({ visitType: 'weightloss' }),
      apiService.getMedications({ visitType: 'antiAging' }),
      // Zofran/Ondansetron lives in its own antiNausea catalog; without this
      // fetch the nausea add-on can never resolve to a SKU.
      apiService.getMedications({ visitType: 'antiNausea' }).catch(() => []),
    ])
      .then(([wl, aa, an]) => { if (!alive) return; setWlMeds(wl); setStandaloneCat([...wl, ...aa, ...an]); })
      .catch((e) => { console.error('catalog fetch failed', e); if (alive) setError('We could not load your treatment right now. Please try again.'); })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [molecule]);

  const base = useMemo(() => {
    if (!wlMeds || !wlMeds.length) return null;
    if (manualBaseId) { const m = wlMeds.find((x) => x.medId === manualBaseId); if (m) return m; }
    return resolveBase(wlMeds, molecule, isNewPatient);
  }, [wlMeds, molecule, isNewPatient, manualBaseId]);

  // Celebrate the recommendation slide once it's ready (primary med resolved).
  useSchoolPrideConfetti(phase === 'recommend' && !!base);

  const baseSkus = useMemo(
    () => (wlMeds || []).filter((m) => !additiveOf(m)).sort((a, b) => (a.name || '').localeCompare(b.name || '') || (a.price || 0) - (b.price || 0)),
    [wlMeds],
  );

  const additiveOptions = useMemo(() => {
    if (!base || !wlMeds) return [];
    return WL_ADDITIVES.map((a) => {
      const compound = findCompound(a, base, wlMeds);
      if (compound) return { ...a, type: 'compound', sku: compound, addedPrice: (compound.price || 0) - (base.price || 0) };
      const standalone = standaloneCat
        .filter((m) => a.standaloneRe.test((m.name || '').trim()) && !/semaglutide|tirzepatide/i.test(m.name || '') && m.requiresSeparateIntake !== true)
        .sort((x, y) => (x.price || 0) - (y.price || 0))[0];
      if (standalone) return { ...a, type: 'standalone', sku: standalone, addedPrice: standalone.price || 0 };
      return null;
    }).filter(Boolean);
  }, [base, wlMeds, standaloneCat]);

  useEffect(() => {
    if (!additiveOptions.length) return;
    const want = chosenKeys(answers);
    additiveOptions.forEach((o) => {
      if (!want.has(o.key)) return;
      if (o.type === 'compound') setCompoundKey((p) => p || o.key);
      else setSeparateIds((p) => (p.includes(o.sku.medId) ? p : [...p, o.sku.medId]));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additiveOptions]);

  const compoundOpt = additiveOptions.find((o) => o.type === 'compound' && o.key === compoundKey);
  const active = compoundOpt ? compoundOpt.sku : base;
  // Add-ons the patient explicitly asked about on the side-effects slide —
  // surfaced on the recommendation slide so the choice is visible before pay.
  const requestedOpts = useMemo(
    () => additiveOptions.filter((o) => chosenKeys(answers).has(o.key)),
    [additiveOptions, answers],
  );
  const separateOpts = additiveOptions.filter((o) => o.type === 'standalone' && separateIds.includes(o.sku.medId));
  const totalCents = (active ? Number(active.price) || 0 : 0) + separateOpts.reduce((s, o) => s + (Number(o.sku.price) || 0), 0);
  const selectedMedIds = active ? [active.medId, ...separateOpts.map((o) => o.sku.medId)] : [];

  const toggle = (o) => {
    if (o.type === 'compound') setCompoundKey((prev) => (prev === o.key ? null : o.key));
    else setSeparateIds((prev) => (prev.includes(o.sku.medId) ? prev.filter((x) => x !== o.sku.medId) : [...prev, o.sku.medId]));
  };
  const isAdded = (o) => (o.type === 'compound' ? compoundKey === o.key : separateIds.includes(o.sku.medId));
  const additiveRow = (o) => {
    const added = isAdded(o);
    return (
      <div key={o.key} className="flex items-center gap-3">
        <MedImage item={o.sku} size="w-12 h-12" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-800">{o.label}</p>
          <p className="text-xs text-[#2d8a5f]">{o.addedPrice > 0 ? `+${fmtUsd(o.addedPrice)}` : 'Included'}{o.type === 'compound' ? ' · in your medication' : ''}</p>
          {o.type === 'standalone' && (
            <p className="text-xs text-gray-400">Separate prescription — ordered together with your treatment</p>
          )}
        </div>
        <button type="button" onClick={() => toggle(o)} data-testid={`addon-${o.key}`} aria-pressed={added}
          className={`text-sm font-medium rounded-full px-3 py-1.5 flex-shrink-0 transition-colors ${added ? 'bg-[#2d8a5f] text-white' : 'bg-[#e8f5ee] text-[#2d8a5f] hover:bg-[#d8efe2]'}`}>
          {added ? 'Added ✓' : '+ Add'}
        </button>
      </div>
    );
  };
  const chooseBase = (medId) => { setManualBaseId(medId); setCompoundKey(null); setModalOpen(false); };
  const submit = (payment) => onComplete({ ...payment, selectedMedIds, selectedMedId: active?.medId, phoneVerifyToken: phoneVerifyToken || undefined });

  if (error) return <p className="max-w-md mx-auto text-center text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="catalog-error">{error}</p>;
  if (wlMeds === null) return <p className="max-w-md mx-auto text-center text-gray-500 py-10" data-testid="catalog-loading">Loading your treatment…</p>;
  if (!base) return <p className="max-w-md mx-auto text-center text-gray-600 py-10" data-testid="catalog-empty">This treatment isn’t currently available. Please check back soon.</p>;

  // Portal to <body> so the fixed overlay escapes the slide-wrapper's retained
  // CSS transform (animation fill-mode: forwards), which would otherwise anchor
  // position:fixed to the wrapper instead of the viewport and clip the list.
  const planModal = modalOpen && createPortal(
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" data-testid="modify-plan-modal" onClick={() => setModalOpen(false)}>
      <div className="bg-white w-full sm:max-w-lg sm:rounded-3xl rounded-t-3xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h3 className="font-semibold text-gray-900">Choose your medication</h3>
            <p className="text-sm text-gray-500">Pick a different strength or brand. Your provider confirms the final choice.</p>
          </div>
          <button type="button" onClick={() => setModalOpen(false)} aria-label="Close" className="text-gray-400 hover:text-gray-600 text-2xl leading-none px-2">×</button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 space-y-2">
          {baseSkus.map((m) => {
            const sel = m.medId === base.medId;
            return (
              <button key={m.medId} type="button" onClick={() => chooseBase(m.medId)} data-testid={`plan-${m.medId}`}
                className={`w-full text-left rounded-2xl border-2 p-3 flex items-center gap-3 transition-all ${sel ? 'border-[#2d8a5f] bg-[#f0faf4]' : 'border-gray-200 hover:border-gray-300'}`}>
                <MedImage item={m} size="w-11 h-11" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 leading-snug">{m.name || m.medication}</p>
                  <p className="text-xs text-gray-500">{[m.strength, m.daySupply ? `${m.daySupply}-day` : null].filter(Boolean).join(' · ')}</p>
                </div>
                <span className="text-sm font-bold text-gray-900 flex-shrink-0">{fmtUsd(m.price)}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  );

  // ---------- PHASE 1: RECOMMENDATION ----------
  if (phase === 'recommend') {
    return (
      <div data-testid="glp-recommend" className="max-w-md mx-auto">
        <div className="mb-6">
          <p className="text-[#2d8a5f] font-semibold text-sm mb-1">Congratulations!</p>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">Your provider will likely recommend treatment.</h2>
          <p className="text-gray-500 text-sm mt-2">Select the treatment you'd like your medical provider to consider prescribing.</p>
        </div>

        <div className="rounded-3xl border-2 border-[#2d8a5f] bg-[#f0faf4] p-5 mb-4">
          <div className="flex items-center gap-4">
            <MedImage item={active} size="w-20 h-20" />
            <div className="min-w-0 flex-1">
              <p className="font-bold text-gray-900 leading-snug">{active.name || active.medication}</p>
              <p className="text-sm text-gray-500">{active.strength}</p>
              <p className="text-lg font-bold text-[#2d8a5f] mt-1">{fmtUsd(active.price)}</p>
              <ProductLearnMore product={active} className="mt-1 inline-block" />
            </div>
          </div>
        </div>

        {requestedOpts.length > 0 && (
          <div className="rounded-2xl border border-gray-200 p-5 mb-4" data-testid="recommend-addons">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Based on your answers</p>
            <p className="text-xs text-gray-400 mb-3">You asked about these — include them for your provider to consider alongside your treatment.</p>
            <div className="space-y-3">{requestedOpts.map(additiveRow)}</div>
            {totalCents !== (Number(active.price) || 0) && (
              <div className="border-t border-gray-100 mt-4 pt-3 flex justify-between items-center text-sm">
                <span className="text-gray-600 font-medium">Total with add-ons</span>
                <span className="font-bold text-[#2d8a5f]" data-testid="recommend-total">{fmtUsd(totalCents)}</span>
              </div>
            )}
          </div>
        )}

        <button type="button" data-testid="select-treatment" onClick={() => setPhase('pay')}
          className="w-full rounded-2xl bg-[#2d8a5f] px-4 py-4 font-semibold text-white hover:bg-[#246f4d] transition-colors mb-2">
          Select this treatment
        </button>
        <button type="button" onClick={() => setModalOpen(true)} data-testid="modify-plan" className="w-full text-center text-sm text-[#2d8a5f] underline underline-offset-2 hover:text-[#236b4a] mb-6">
          See other options
        </button>

        <div className="rounded-2xl bg-gray-50 p-5 mb-4">
          <h3 className="font-semibold text-gray-900 mb-3 text-sm">About your medication</h3>
          <ul className="space-y-2.5 text-sm text-gray-600">
            <li className="flex gap-2"><Check /> Every active ingredient is reviewed through our quality &amp; compliance program</li>
            <li className="flex gap-2"><Check /> Custom-formulated by licensed U.S. pharmacies</li>
            <li className="flex gap-2"><Check /> Physician oversight with dosing personalized to you</li>
          </ul>
        </div>

        {outlook && (
          <div className="rounded-2xl bg-gradient-to-br from-[#f0faf4] to-white border border-[#2d8a5f]/15 p-5 text-center">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Your health journey outlook</p>
            <p className="text-4xl font-extrabold text-[#2d8a5f] leading-none">{outlook.target}<span className="text-lg font-semibold text-gray-400"> lbs</span></p>
            <p className="text-sm text-gray-500 mt-1">is achievable — about {outlook.loss} lbs from your current {outlook.current} lbs.</p>
            <div className="mt-4 h-2 rounded-full bg-gray-100 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#2d8a5f] to-[#4cb37e]" style={{ width: `${Math.min(100, Math.round((outlook.loss / outlook.current) * 100) * 3)}%` }} />
            </div>
          </div>
        )}
        {planModal}
      </div>
    );
  }

  // ---------- PHASE 2: CHECKOUT ----------
  return (
    <div data-testid="glp-catalog-checkout" className="max-w-md mx-auto">
      <div className="mb-5">
        <p className="text-[#2d8a5f] font-semibold text-sm">You're in!</p>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">Last step before getting real results.</h2>
      </div>

      {/* Plan summary card */}
      <div className="rounded-3xl border border-[#2d8a5f]/30 bg-[#f0faf4] p-5 mb-3">
        <div className="flex items-center gap-4 pb-4 border-b border-[#2d8a5f]/15">
          <MedImage item={active} size="w-16 h-16" />
          <div className="min-w-0 flex-1">
            <p className="font-bold text-gray-900 leading-snug" data-testid="primary-name">{active.name || active.medication}</p>
            <p className="text-sm text-gray-500">{[active.strength, active.daySupply ? `${active.daySupply}-day supply` : null].filter(Boolean).join(' · ')}</p>
            <ProductLearnMore product={active} className="mt-0.5 inline-block" />
          </div>
          <span className="font-bold text-gray-900 flex-shrink-0">{fmtUsd(active.price)}</span>
        </div>
        <ul className="grid grid-cols-1 gap-2 pt-4 text-sm text-gray-700">
          <li className="flex gap-2"><Check /> Provider review &amp; prescription included</li>
          <li className="flex gap-2"><Check /> Free, discreet shipping</li>
          <li className="flex gap-2"><Check /> Unlimited care-team messaging</li>
          <li className="flex gap-2"><Check /> No long-term contract — cancel anytime</li>
        </ul>
        <button type="button" onClick={() => setModalOpen(true)} data-testid="modify-plan" className="mt-3 text-sm text-[#2d8a5f] underline underline-offset-2 hover:text-[#236b4a]">
          Modify your plan
        </button>
      </div>

      <ProviderReviewNotice className="mb-4" />
      {/* Trust banner */}
      <div className="flex items-center gap-3 rounded-2xl bg-[#fff7ed] border border-[#fed7aa] p-4 mb-4">
        <div className="w-9 h-9 rounded-full bg-[#fed7aa] flex items-center justify-center flex-shrink-0">
          <svg className="w-5 h-5 text-[#c2410c]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
        </div>
        <p className="text-sm text-gray-700"><span className="font-semibold">Free expedited shipping</span> — your treatment ships in 2–3 days once your provider prescribes it.</p>
      </div>

      {/* Additives */}
      {additiveOptions.length > 0 && (
        <div className="rounded-2xl border border-gray-200 p-5 mb-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Frequently added together</p>
          <p className="text-xs text-gray-400 mb-3">Compounded into your medication or prescribed alongside it — no extra visit needed.</p>
          <div className="space-y-3">{additiveOptions.map(additiveRow)}</div>
        </div>
      )}

      {/* Order total */}
      <div className="rounded-2xl border border-gray-200 p-5 mb-4">
        {separateOpts.map((o) => (
          <div key={o.sku.medId} className="flex justify-between text-sm mb-2">
            <span className="text-gray-600">{o.label}</span><span className="font-medium">{fmtUsd(o.sku.price)}</span>
          </div>
        ))}
        <div className="flex justify-between text-sm mb-2">
          <span className="text-gray-600">{active.name || active.medication}</span><span className="font-medium">{fmtUsd(active.price)}</span>
        </div>
        <MembershipNotice />
        <div className="border-t border-gray-200 mt-2 pt-3 flex justify-between items-center">
          <span className="font-semibold text-lg">Total</span>
          <span className="text-2xl font-bold text-[#2d8a5f]" data-testid="cart-total">{fmtUsd(totalCents)}</span>
        </div>
      </div>

      {/* Payment */}
      <div className="rounded-2xl border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-900 mb-4">Payment details</h3>
        {/* §otp: phone was verified on the contact slide; phoneVerifyToken
            (from the basic-contact answer) is relayed to the payment below. */}
        {stagingTest ? (
          <div className="space-y-3">
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
              ⚠️ Staging sign-off mode — Square is bypassed. Submits a test visit to Beluga <strong>staging</strong> with no charge.
            </p>
            <button type="button" data-testid="staging-bypass-pay" disabled={isProcessing} onClick={() => submit({ paymentId: 'STAGING_TEST_BYPASS' })}
              className="w-full rounded-xl bg-[#2d8a5f] px-4 py-3 font-semibold text-white disabled:opacity-50">
              {isProcessing ? 'Submitting…' : `[Staging test] Skip payment & submit (${fmtUsd(totalCents)})`}
            </button>
          </div>
        ) : (
          <>
          {/* §concierge-oto: the GLP-1 flow has its own checkout, so it needs the same
              card-save notice + card-keep signal as CatalogCheckoutStep, or GLP-1
              patients never reach the post-payment offer. */}
          <OtoCardSaveNotice answers={answers} visitType="weightloss" />
          <SquarePaymentForm planId={null} selectedMedIds={selectedMedIds} phoneVerifyToken={phoneVerifyToken} amount={totalCents} onComplete={submit}
            cardOnFile={otoCardOnFile(answers, 'weightloss')} otoCardOnFile={otoCardOnFile(answers, 'weightloss')}
            answers={answers} setLoading={setLoading} clearSavedProgress={clearSavedProgress} primaryGoal={primaryGoal}
            currentStep={currentStep} timestamp={timestamp} email={email} />
          </>
        )}
      </div>
      {planModal}
    </div>
  );
};

export default GlpCatalogCheckoutStep;
